/**
 * CashSync debt domain hooks. Same pattern as `use-finance-query`:
 * the factory's canonical queryKey + encapsulated Drizzle query builders.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@cashsync/powersync";

import {
  type DebtPaymentRow,
  type DebtWithPaid,
  getDebtPaymentsQuery,
  getDebtsWithPaidQuery,
  queryKeys,
} from "../index";

import { createPowerSyncQuery, DEFAULT_THROTTLE_MS } from "./create-power-sync-query";

interface BaseDebtsQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

/** Live debts with a SUM of payments (`paid`) to derive the remaining balance. */
export function useDebtsQuery(options: BaseDebtsQueryOptions) {
  return createPowerSyncQuery<DebtWithPaid>({
    queryKey: () => queryKeys.debts.list(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getDebtsWithPaidQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

interface UseDebtPaymentsQueryOptions extends BaseDebtsQueryOptions {
  debtId: Accessor<string | undefined>;
}

/** Payments for a debt, from newest to oldest. */
export function useDebtPaymentsQuery(options: UseDebtPaymentsQueryOptions) {
  return createPowerSyncQuery<DebtPaymentRow>({
    queryKey: () => queryKeys.debts.payments(options.debtId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.debtId();
      return db && id ? getDebtPaymentsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.debtId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
